class HeroCarousel extends HTMLElement {
  connectedCallback() {
    if (this.swiper || this.pending) return;

    this.pending = true;
    this.lifecycle?.abort();
    this.lifecycle = new AbortController();

    // Later slides stay unrendered until load (or 4s at most) so their images don't compete with the first slide.
    if (document.readyState === 'complete') {
      this.hydrate();
    } else {
      window.addEventListener('load', () => this.hydrate(), {
        once: true,
        signal: this.lifecycle.signal,
      });
      const timer = setTimeout(() => this.hydrate(), 4000);
      this.lifecycle.signal.addEventListener('abort', () => clearTimeout(timer));
    }

    if (window.Swiper) {
      this.init();
    } else {
      window.addEventListener('load', () => this.init(), {
        once: true,
        signal: this.lifecycle.signal,
      });
    }
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.lifecycle?.abort();
    this.swiper?.destroy(true, true);
    this.swiper = null;
    this.pending = false;
  }

  hydrate() {
    if (this.classList.contains('is-hydrated')) return;

    this.classList.add('is-hydrated');
    this.startAutoplay();
  }

  startAutoplay() {
    if (!this.autoplay || !this.classList.contains('is-hydrated') || !this.swiper?.autoplay) return;

    this.swiper.autoplay.start();
    // A pointer already resting on the carousel never fired pointerenter for the
    // running timer, so pause here and let Swiper's pointerleave resume it.
    if (this.matches(':hover')) this.swiper.autoplay.pause();
  }

  init() {
    if (this.swiper || !window.Swiper) return;

    const slides = [...this.querySelectorAll('.hero-carousel__slide')];
    if (slides.length < 2) {
      this.pending = false;
      this.classList.add('is-ready');
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = this.dataset.autoplay === 'true' && !reduceMotion;
    const delay = Number(this.dataset.delay) || 7000;
    const counter = this.querySelector('.hero-carousel__counter');

    this.autoplay = autoplay;
    this.listeners = new AbortController();
    this.swiper = new Swiper(this, {
      slidesPerView: 1,
      speed: 450,
      rewind: true,
      navigation: {
        prevEl: this.querySelector('.hero-carousel__prev'),
        nextEl: this.querySelector('.hero-carousel__next'),
      },
      autoplay: autoplay
        ? {
            delay,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
    });

    if (!this.classList.contains('is-hydrated')) this.swiper.autoplay?.stop();

    const update = () => {
      const active = this.swiper.realIndex ?? this.swiper.activeIndex;
      counter.textContent = `${active + 1} / ${slides.length}`;
      slides.forEach((slide, index) => {
        const hidden = index !== active;
        slide.setAttribute('aria-hidden', String(hidden));
        if ('inert' in slide) slide.inert = hidden;
      });
    };

    this.swiper.on('slideChange', update);
    // Render the other slides as soon as someone starts dragging, not after release.
    this.swiper.on('touchStart', () => this.hydrate());
    this.swiper.on('slideChangeTransitionStart', () => this.hydrate());
    this.addEventListener('focusin', () => this.swiper.autoplay?.stop(), {
      signal: this.listeners.signal,
    });
    this.addEventListener(
      'focusout',
      (event) => {
        if (!this.contains(event.relatedTarget)) this.startAutoplay();
      },
      { signal: this.listeners.signal },
    );
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          this.swiper.autoplay?.stop();
        } else {
          this.startAutoplay();
        }
      },
      { signal: this.listeners.signal },
    );

    update();
    this.classList.add('is-ready');
    this.pending = false;
  }
}

if (!customElements.get('hero-carousel')) {
  customElements.define('hero-carousel', HeroCarousel);
}
